import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, SparklesIcon } from 'lucide-react';
import { EncabezadoPagina } from '../components/layout/AppLayout';
import { EncabezadoPanel, Panel } from '../components/ui/Basicos';
import { CodigoQR } from '../components/ui/CodigoQR';
import { useDatos } from '../contexts/DatosContext';
import { puedeRecibirExpedientes } from '../utils/personal';
import { AUTORIDADES, MATERIAS, TIPOS_ASUNTO } from '../data/catalogos';
import { estiloColor } from '../data/flujos';
import type { Materia } from '../types';
import { fechaRel } from '../utils/fechas';
import { nivelOcupacion } from '../utils/almacen';
const claseInput = 'h-10 w-full rounded border border-gris-200 px-3 text-sm text-gris-800 placeholder:text-gris-400 focus:border-marino-500';
const claseEtiqueta = 'block text-xs font-semibold text-gris-600';
export function NuevoExpediente() {
  const navigate = useNavigate();
  const {
    agregarExpediente,
    generarLocalizador,
    expedientes,
    etapasDe,
    sucursales,
    almacenes,
    abogados
  } = useDatos();
  const [soloDeLaSucursal, setSoloDeLaSucursal] = useState(true);
  const [soloDeLaMateria, setSoloDeLaMateria] = useState(true);
  const [cliente, setCliente] = useState('');
  const [contacto, setContacto] = useState('');
  const [materia, setMateria] = useState<Materia | ''>('');
  const [tipoAsunto, setTipoAsunto] = useState('');
  const [autoridad, setAutoridad] = useState('');
  const [numeroJudicial, setNumeroJudicial] = useState('');
  const [sucursalId, setSucursalId] = useState('');
  const [abogadoIds, setAbogadoIds] = useState<string[]>([]);
  const [resumen, setResumen] = useState('');
  const [almacenId, setAlmacenId] = useState('');
  const [pasilloId, setPasilloId] = useState('');
  const [estanteId, setEstanteId] = useState('');
  const [cajaId, setCajaId] = useState('');
  const [error, setError] = useState('');
  const almacenesSucursal = almacenes.filter(a => a.sucursalId === sucursalId);
  const almacenSel = almacenesSucursal.find(a => a.id === almacenId);
  const pasilloSel = almacenSel?.pasillos.find(p => p.id === pasilloId);
  const estanteSel = pasilloSel?.estantes.find(e => e.id === estanteId);
  const cajaSel = estanteSel?.cajas.find(c => c.id === cajaId);
  const ubicacion = {
    almacen: almacenSel?.nombre ?? '',
    pasillo: pasilloSel?.nombre ?? '',
    estante: estanteSel?.nombre ?? '',
    caja: cajaSel?.nombre ?? ''
  };
  const ocupacionCaja = cajaSel ? expedientes.filter(e => e.sucursalId === sucursalId && e.ubicacion.almacen === ubicacion.almacen && e.ubicacion.pasillo === ubicacion.pasillo && e.ubicacion.estante === ubicacion.estante && e.ubicacion.caja === ubicacion.caja).length : 0;
  const localizador = useMemo(() => sucursalId && materia ? generarLocalizador(sucursalId, materia as Materia) : '', [sucursalId, materia, generarLocalizador]);
  const etapasFlujo = materia ? etapasDe(materia as Materia) : [];
  const abogadosDisponibles = abogados.filter(a => {
    if (!puedeRecibirExpedientes(a)) return false;
    if (soloDeLaSucursal && sucursalId && a.sucursalId !== sucursalId) return false;
    if (soloDeLaMateria && materia && !a.especialidades.includes(materia as Materia)) return false;
    return true;
  });
  const alternarAbogado = (id: string) => setAbogadoIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const guardar = (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!cliente || !materia || !sucursalId || !tipoAsunto || abogadoIds.length === 0 || !cajaSel) {
      setError('Completa cliente, materia, tipo de asunto, sucursal, al menos un abogado y la ubicación física completa (almacén, pasillo, estante y caja).');
      return;
    }
    const id = `ex-${expedientes.length + 1}-${Date.now()}`;
    agregarExpediente({
      id,
      localizador,
      cliente,
      clienteContacto: contacto || 'Sin datos de contacto registrados',
      materia: materia as Materia,
      tipoAsunto,
      autoridad: autoridad || 'Por determinar',
      numeroJudicial: numeroJudicial || 'Sin número asignado',
      sucursalId,
      abogadoIds,
      ubicacion,
      estado: 'Activo',
      fechaApertura: fechaRel(0),
      etapaActual: 0,
      resumen: resumen || 'Expediente recién aperturado, pendiente de integrar documentación.'
    });
    navigate(`/expedientes/${id}`);
  };
  return <>
      <EncabezadoPagina titulo="Nuevo expediente" descripcion="Al guardar se genera el localizador con el formato SUCURSAL-MATERIA-AÑO-CONSECUTIVO." acciones={<Link to="/expedientes" className="inline-flex h-9 items-center gap-2 rounded border border-gris-200 bg-white px-3 text-sm font-medium text-gris-600 transition-colors duration-150 hover:border-gris-300" data-id="element-1577" data-mp="29y4hm2bequ30">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1578" data-mp="1vtxdpczsdayd" />
            Volver
          </Link>} data-id="element-1576" data-mp="87klskvvfns9" />

      <form onSubmit={guardar} className="grid gap-4 xl:grid-cols-3" data-id="element-1579" data-mp="1zwk3z9gt6ntg">
        <div className="space-y-4 xl:col-span-2" data-id="element-1580" data-mp="1m32hbfstsuz7">
          <Panel data-id="element-1581" data-mp="1r1vvlafxsq0f">
            <EncabezadoPanel titulo="Cliente y asunto" data-id="element-1582" data-mp="1r1drxsqy39ih" />
            <div className="grid gap-4 p-5 md:grid-cols-2" data-id="element-1583" data-mp="1c54u8vmpd486">
              <label className="md:col-span-2" data-id="element-1584" data-mp="1c3ldz511qt6e">
                <span className={claseEtiqueta} data-id="element-1585" data-mp="1h3znysm7kd72">Cliente *</span>
                <input className={`mt-1.5 ${claseInput}`} value={cliente} onChange={e => setCliente(e.target.value)} placeholder="Nombre completo o razón social" data-id="element-1586" data-mp="1h5j5emjlouaf" />
              </label>
              <label className="md:col-span-2" data-id="element-1587" data-mp="2xjstdfjxkh0q">
                <span className={claseEtiqueta} data-id="element-1588" data-mp="32hkik1t6xp18">Contacto</span>
                <input className={`mt-1.5 ${claseInput}`} value={contacto} onChange={e => setContacto(e.target.value)} placeholder="Persona de contacto, teléfono o correo" data-id="element-1589" data-mp="32h2ggojukgle" />
              </label>
              <label data-id="element-1590" data-mp="2r30petuzjftf">
                <span className={claseEtiqueta} data-id="element-1591" data-mp="2r2his9ascdup">Materia *</span>
                <select className={`mt-1.5 ${claseInput}`} value={materia} onChange={e => {
                setMateria(e.target.value as Materia);
                setTipoAsunto('');
              }} data-id="element-1592" data-mp="2m46a41w889e2">
                  <option value="" data-id="element-1593" data-mp="3a0jz7zp9p2gw">Selecciona una materia</option>
                  {MATERIAS.map(m => <option key={m} value={m} data-id="element-1594" data-mp="352sa1dg0buge">
                      {m}
                    </option>)}
                </select>
              </label>
              <label data-id="element-1595" data-mp="1la63h3cv7l9">
                <span className={claseEtiqueta} data-id="element-1596" data-mp="1ks2fzed5r3b">Tipo de asunto *</span>
                <select className={`mt-1.5 ${claseInput}`} value={tipoAsunto} onChange={e => setTipoAsunto(e.target.value)} disabled={!materia} data-id="element-1597" data-mp="8wg3ul5sl003">
                  <option value="" data-id="element-1598" data-mp="iucllvt8it76">
                    {materia ? 'Selecciona el tipo' : 'Elige primero la materia'}
                  </option>
                  {materia && TIPOS_ASUNTO[materia as Materia].map(t => <option key={t} value={t} data-id="element-1599" data-mp="rt7u5ejr4fdn">
                        {t}
                      </option>)}
                </select>
              </label>
              <label data-id="element-1600" data-mp="11mf0b1mbfzes">
                <span className={claseEtiqueta} data-id="element-1601" data-mp="11n6ty6wcrn2f">Autoridad</span>
                <select className={`mt-1.5 ${claseInput}`} value={autoridad} onChange={e => setAutoridad(e.target.value)} data-id="element-1602" data-mp="16j5flmqaqqz5">
                  <option value="" data-id="element-1603" data-mp="1flwqnlf9fkq9">Por determinar</option>
                  {AUTORIDADES.map(a => <option key={a} value={a} data-id="element-1604" data-mp="20uijqi9u8wid">
                      {a}
                    </option>)}
                </select>
              </label>
              <label data-id="element-1605" data-mp="2atrdba0sk947">
                <span className={claseEtiqueta} data-id="element-1606" data-mp="2asyagk4ye3rc">Número de expediente judicial</span>
                <input className={`mt-1.5 ${claseInput} font-mono`} value={numeroJudicial} onChange={e => setNumeroJudicial(e.target.value)} placeholder="Ej. 452/2026-I" data-id="element-1607" data-mp="25uel09r5i9u8" />
              </label>
              <label className="md:col-span-2" data-id="element-1608" data-mp="1ruvh5t5dhknq">
                <span className={claseEtiqueta} data-id="element-1609" data-mp="1rwq3nbwalktx">Resumen del asunto</span>
                <textarea className="mt-1.5 w-full rounded border border-gris-200 px-3 py-2 text-sm text-gris-800 placeholder:text-gris-400 focus:border-marino-500" rows={3} value={resumen} onChange={e => setResumen(e.target.value)} placeholder="Antecedentes y pretensión principal" data-id="element-1610" data-mp="1n459g99h55pv" />
              </label>
            </div>
          </Panel>

          <Panel data-id="element-1611" data-mp="3avwefsttpvx3">
            <EncabezadoPanel titulo="Sucursal y abogados asignados" descripcion="La sucursal determina la clave del localizador y los abogados disponibles." data-id="element-1612" data-mp="2x2ewgcgscr96" />
            <div className="p-5" data-id="element-1613" data-mp="3otkwkswiaoyg">
              <label className="block max-w-sm" data-id="element-1614" data-mp="3ounnv1xqqtew">
                <span className={claseEtiqueta} data-id="element-1615" data-mp="3orrqpftpw8ri">Sucursal *</span>
                <select className={`mt-1.5 ${claseInput}`} value={sucursalId} onChange={e => {
                setSucursalId(e.target.value);
                setAbogadoIds([]);
                setAlmacenId('');
                setPasilloId('');
                setEstanteId('');
                setCajaId('');
              }} data-id="element-1616" data-mp="3tre0tge918w3">
                  <option value="" data-id="element-1617" data-mp="29arbeb67gaet">Selecciona una sucursal</option>
                  {sucursales.filter(s => s.activa).map(s => <option key={s.id} value={s.id} data-id="element-1618" data-mp="35pnrrluwt0uc">
                        {s.nombre} ({s.clave})
                      </option>)}
                </select>
              </label>

              <fieldset className="mt-5" data-id="element-1619" data-mp="2wwaqnwmoysx6">
                <legend className={claseEtiqueta} data-id="element-1620" data-mp="2i0l2psxy45pt">Abogados asignados *</legend>
                <div className="mt-2 flex flex-wrap items-center gap-4" data-id="element-1621" data-mp="2i2pailyytovd">
                  {[{
                  etiqueta: 'Solo de esta sucursal',
                  valor: soloDeLaSucursal,
                  set: setSoloDeLaSucursal
                }, {
                  etiqueta: 'Solo quienes manejan la materia',
                  valor: soloDeLaMateria,
                  set: setSoloDeLaMateria
                }].map(filtro => <label key={filtro.etiqueta} className="inline-flex cursor-pointer items-center gap-2 text-xs text-gris-600" data-id="element-1622" data-mp="mb1qwarn8k78">
                      <input type="checkbox" checked={filtro.valor} onChange={e => filtro.set(e.target.checked)} className="h-4 w-4 rounded border-gris-300 text-marino-700" data-id="element-1623" data-mp="v4et63blky5z" />
                      {filtro.etiqueta}
                    </label>)}
                  <span className="tabular text-2xs text-gris-400" data-id="element-1624" data-mp="1e2qz7g77prxc">
                    {abogadosDisponibles.length} disponibles
                  </span>
                </div>
                {abogadosDisponibles.length === 0 && <p className="mt-3 rounded border border-ambar-100 bg-ambar-50 px-3 py-2 text-xs text-ambar-700" data-id="element-1625" data-mp="3fv5h4f9rawdr">
                    Ningún abogado activo cumple los filtros. Quita un filtro o da de alta personal
                    en «Abogados y personal».
                  </p>}
                <div className="mt-3 grid gap-2 sm:grid-cols-2" data-id="element-1626" data-mp="1js0rn567iq6d">
                  {abogadosDisponibles.map(a => <label key={a.id} className={`flex cursor-pointer items-start gap-3 rounded border px-3 py-2.5 transition-colors duration-150 ${abogadoIds.includes(a.id) ? 'border-marino-700 bg-marino-50' : 'border-gris-200 hover:border-gris-300'}`} data-id="element-1627" data-mp="1oohfdy9hv2ix">
                      <input type="checkbox" checked={abogadoIds.includes(a.id)} onChange={() => alternarAbogado(a.id)} className="mt-0.5 h-4 w-4 rounded border-gris-300 text-marino-700" data-id="element-1628" data-mp="27imy4s0kc2e9" />
                      <span className="min-w-0" data-id="element-1629" data-mp="2ldysgyat3qun">
                        <span className="block truncate text-sm font-medium text-gris-800" data-id="element-1630" data-mp="9u0yj0n29vy1">
                          {a.nombre}
                        </span>
                        <span className="block text-2xs text-gris-500" data-id="element-1631" data-mp="s9wq6vn699g">
                          Cédula {a.cedula} · {a.especialidades.join(', ')}
                        </span>
                      </span>
                    </label>)}
                </div>
              </fieldset>
            </div>
          </Panel>

          <Panel data-id="element-1632" data-mp="oix5euddt2x2">
            <EncabezadoPanel titulo="Ubicación física" descripcion="Solo se ofrecen las ubicaciones dadas de alta en Configuración → Sucursales y almacenes." data-id="element-1633" data-mp="oidys9t6m0yc" />
            <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1634" data-mp="erjc54c0wzcd">
              <label data-id="element-1635" data-mp="1c7q8dasf8b16">
                <span className={claseEtiqueta} data-id="element-1636" data-mp="1c919f2uhf4ol">Almacén *</span>
                <select className={`mt-1.5 ${claseInput}`} value={almacenId} onChange={e => {
                setAlmacenId(e.target.value);
                setPasilloId('');
                setEstanteId('');
                setCajaId('');
              }} disabled={!sucursalId} data-id="element-1637" data-mp="17g83ibna1qax">
                  <option value="" data-id="element-1638" data-mp="mzdmta8635ow">{sucursalId ? 'Selecciona' : 'Elige sucursal'}</option>
                  {almacenesSucursal.map(a => <option key={a.id} value={a.id} data-id="element-1639" data-mp="1aqj6y1l54k50">
                      {a.nombre}
                    </option>)}
                </select>
              </label>
              <label data-id="element-1640" data-mp="10yddsoj5jwo8">
                <span className={claseEtiqueta} data-id="element-1641" data-mp="10yvhg6859d66">Pasillo *</span>
                <select className={`mt-1.5 ${claseInput}`} value={pasilloId} onChange={e => {
                setPasilloId(e.target.value);
                setEstanteId('');
                setCajaId('');
              }} disabled={!almacenSel} data-id="element-1642" data-mp="15xyfv7tmcl56">
                  <option value="" data-id="element-1643" data-mp="3j33d9flc8ebw">{almacenSel ? 'Selecciona' : 'Elige almacén'}</option>
                  {(almacenSel?.pasillos ?? []).map(p => <option key={p.id} value={p.id} data-id="element-1644" data-mp="3rxrjp92ng22a">
                      {p.nombre}
                    </option>)}
                </select>
              </label>
              <label data-id="element-1645" data-mp="30625jn0eg049">
                <span className={claseEtiqueta} data-id="element-1646" data-mp="305k50e6pezqj">Estante *</span>
                <select className={`mt-1.5 ${claseInput}`} value={estanteId} onChange={e => {
                setEstanteId(e.target.value);
                setCajaId('');
              }} disabled={!pasilloSel} data-id="element-1647" data-mp="2v78t7xwumf5o">
                  <option value="" data-id="element-1648" data-mp="ogj651eyfodd">{pasilloSel ? 'Selecciona' : 'Elige pasillo'}</option>
                  {(pasilloSel?.estantes ?? []).map(es => <option key={es.id} value={es.id} data-id="element-1649" data-mp="jhg8w357umfy">
                      {es.nombre}
                    </option>)}
                </select>
              </label>
              <label data-id="element-1650" data-mp="3i7fzcw8s7a7n">
                <span className={claseEtiqueta} data-id="element-1651" data-mp="3iavica5kt3ec">Caja *</span>
                <select className={`mt-1.5 ${claseInput}`} value={cajaId} onChange={e => setCajaId(e.target.value)} disabled={!estanteSel} data-id="element-1652" data-mp="3mbfepbibs2zf">
                  <option value="" data-id="element-1653" data-mp="2ul169ut2ziv5">{estanteSel ? 'Selecciona' : 'Elige estante'}</option>
                  {(estanteSel?.cajas ?? []).map(c => <option key={c.id} value={c.id} data-id="element-1654" data-mp="33jctdmuqnyiu">
                      {c.nombre} (capacidad {c.capacidad})
                    </option>)}
                </select>
                {cajaSel && <span className={`tabular mt-1 block text-2xs ${nivelOcupacion(ocupacionCaja, cajaSel.capacidad) === 'llena' ? 'font-semibold text-peligro-600' : nivelOcupacion(ocupacionCaja, cajaSel.capacidad) === 'casi' ? 'font-semibold text-ambar-700' : 'text-gris-500'}`} data-id="element-1655" data-mp="2au5ycxuhtr2c">
                    Ocupación actual: {ocupacionCaja}/{cajaSel.capacidad}
                    {ocupacionCaja >= cajaSel.capacidad && ' · caja llena, elige otra'}
                  </span>}
              </label>
            </div>
          </Panel>
        </div>

        <div className="space-y-4" data-id="element-1656" data-mp="1kj3tn3jg3v2e">
          <Panel className="xl:sticky xl:top-24" data-id="element-1657" data-mp="1flu4zq3vrnfm">
            <EncabezadoPanel titulo="Etiqueta del expediente" descripcion="Vista previa" data-id="element-1658" data-mp="1fmdeqjjdn5ik" />
            <div className="p-5" data-id="element-1659" data-mp="1aoln7qmn9lew">
              <div className="flex items-center gap-4 rounded-lg border border-dashed border-gris-300 bg-gris-50 p-4" data-id="element-1660" data-mp="1al69os8mckfl">
                <CodigoQR valor={localizador || 'SIN-LOCALIZADOR'} tamano={84} data-id="element-1661" data-mp="11mkoepbrirb4" />
                <div className="min-w-0" data-id="element-1662" data-mp="2pj0rflrcsbzh">
                  <p className="text-2xs uppercase tracking-wide text-gris-400" data-id="element-1663" data-mp="2pk24twvudakd">Localizador</p>
                  <p className="break-all font-mono text-lg font-semibold text-marino-900" data-id="element-1664" data-mp="2uh28y294s38u">
                    {localizador || '— — —'}
                  </p>
                  <p className="mt-1 flex items-center gap-1.5 text-2xs text-gris-500" data-id="element-1665" data-mp="2kjxk1unwhtoc">
                    <SparklesIcon className="h-3.5 w-3.5 text-ambar-500" aria-hidden="true" data-id="element-1666" data-mp="2kls7bfmn9xvl" />
                    Se genera automáticamente
                  </p>
                </div>
              </div>

              <dl className="mt-4 space-y-3 text-sm" data-id="element-1667" data-mp="2bl2fso4f2tno">
                <div data-id="element-1668" data-mp="1wvo3p89ghy4l">
                  <dt className="text-2xs uppercase tracking-wide text-gris-400" data-id="element-1669" data-mp="1ww67cpyg7emj">Cliente</dt>
                  <dd className="text-gris-800" data-id="element-1670" data-mp="21tzgnrv1sor1">{cliente || 'Pendiente'}</dd>
                </div>
                <div data-id="element-1671" data-mp="1nu6qbxnfoajl">
                  <dt className="text-2xs uppercase tracking-wide text-gris-400" data-id="element-1672" data-mp="1nw10cgx62i8w">Ubicación</dt>
                  <dd className="text-gris-800" data-id="element-1673" data-mp="1suud274mq580">
                    {cajaSel ? `${ubicacion.almacen} · ${ubicacion.pasillo} · ${ubicacion.estante} · ${ubicacion.caja}` : 'Pendiente'}
                  </dd>
                </div>
              </dl>

              <div className="mt-4 rounded border border-gris-200 bg-gris-50 px-3 py-3" data-id="element-1674" data-mp="1i8z3f47drgx5">
                <p className="text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-1675" data-mp="1e4rep70xa0yx">
                  Flujo de etapas que se cargará
                </p>
                {!materia ? <p className="mt-1.5 text-xs text-gris-500" data-id="element-1676" data-mp="1n67p3erqm335">
                    Elige la materia para cargar automáticamente su flujo configurado.
                  </p> : etapasFlujo.length === 0 ? <p className="mt-1.5 text-xs text-ambar-700" data-id="element-1677" data-mp="1wyn6a9v7mnmz">
                    La materia {materia} no tiene etapas configuradas. Pide a un administrador que
                    las defina en Flujos de proceso.
                  </p> : <ol className="mt-2 space-y-1.5" data-id="element-1678" data-mp="2iex9ntde60ib">
                    {etapasFlujo.map((et, i) => <li key={et.id} className="flex items-start gap-2" data-id="element-1679" data-mp="2edll7r697lbp" data-mpi={i}>
                        <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${estiloColor(et.color).punto}`} aria-hidden="true" data-id="element-1680" data-mp="2seegwf8akmge" />
                        <span className="min-w-0" data-id="element-1681" data-mp="1zopb7okmzy8g">
                          <span className={`block text-xs ${i === 0 ? 'font-semibold text-marino-800' : 'text-gris-600'}`} data-id="element-1682" data-mp="1zn4js7tmcvet">
                            {et.nombre}
                            {i === 0 && ' · etapa inicial'}
                          </span>
                          {et.tienePlazo && <span className="tabular block text-2xs text-gris-400" data-id="element-1683" data-mp="1c0659fp27xjj">
                              Plazo sugerido: {et.diasSugeridos} días
                            </span>}
                        </span>
                      </li>)}
                  </ol>}
              </div>

              {error && <p role="alert" className="mt-4 rounded border border-peligro-100 bg-peligro-50 px-3 py-2 text-xs text-peligro-700" data-id="element-1684" data-mp="3ekqoyiguwzud">
                  {error}
                </p>}

              <button type="submit" className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded bg-marino-800 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-1685" data-mp="f6dgw5ts5gew">
                <SaveIcon className="h-4 w-4" aria-hidden="true" data-id="element-1686" data-mp="2ly5wtfsk4jpr" />
                Guardar y generar localizador
              </button>
            </div>
          </Panel>
        </div>
      </form>
    </>;
}